/**
 * WeekScheduleOverridesCard — Permite a un perito definir excepciones
 * por semana ISO sobre su horario base. Útil para temporadas (horario de
 * verano, jornada intensiva, etc.).
 *
 * Cada override se guarda en `/api/calendar/schedule/{perito_id}/week-overrides`
 * (PUT) y se borra con DELETE. Sólo se aplica a la semana ISO indicada.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, PencilSimple, Trash, Plus, X, Copy } from "@phosphor-icons/react";

const DAYS = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miércoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];

// ---------- Helpers ISO Week ----------
/** Lunes de la semana ISO (year, week). */
function isoWeekToMonday(year, week) {
  // Empieza por el 4 de enero (siempre está en la sem 1 ISO).
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;        // 1=lun..7=dom
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));
  const result = new Date(week1Monday);
  result.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return result;
}

function fmtMonday(year, week) {
  try {
    const m = isoWeekToMonday(year, week);
    return m.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
  } catch { return "—"; }
}

function currentIsoYearWeek() {
  const now = new Date();
  const target = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((target - yearStart) / 86400000) + 1) / 7);
  return { year: target.getUTCFullYear(), week };
}

function weeksInISOYear(year) {
  // Una semana ISO 53 existe si el año empieza o termina en jueves (o
  // ambos en años bisiestos). Calcula contando.
  const lastDay = new Date(Date.UTC(year, 11, 28)); // 28 dic siempre está en última semana
  const day = lastDay.getUTCDay() || 7;
  lastDay.setUTCDate(lastDay.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(lastDay.getUTCFullYear(), 0, 1));
  return Math.ceil((((lastDay - yearStart) / 86400000) + 1) / 7);
}

const emptyDay = () => ({
  enabled: false, start: "09:00", end: "18:00", lunch_start: "14:00", lunch_end: "15:00",
});

export default function WeekScheduleOverridesCard({ peritoId, schedule, onChanged }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(null);    // { iso_year, iso_week, label, days:{...} }
  const [saving, setSaving] = useState(false);

  const overrides = useMemo(
    () => Array.isArray(schedule?.week_overrides) ? schedule.week_overrides : [],
    [schedule],
  );

  const startNew = () => {
    const { year, week } = currentIsoYearWeek();
    setDraft({
      iso_year: year,
      iso_week: week,
      label: "",
      days: Object.fromEntries(DAYS.map((d) => [d.key, emptyDay()])),
    });
    setEditorOpen(true);
  };

  const startEdit = (ov) => {
    setDraft({
      iso_year: ov.iso_year,
      iso_week: ov.iso_week,
      label: ov.label || "",
      days: Object.fromEntries(DAYS.map((d) => [d.key, ov[d.key] || emptyDay()])),
    });
    setEditorOpen(true);
  };

  const copyFromBase = () => {
    if (!schedule || !draft) return;
    const next = { ...draft };
    DAYS.forEach(({ key }) => {
      const base = schedule[key] || {};
      next.days[key] = {
        enabled: !!base.enabled,
        start: base.start || "09:00",
        end: base.end || "18:00",
        lunch_start: base.lunch_start || "",
        lunch_end: base.lunch_end || "",
      };
    });
    setDraft(next);
    toast("Cargado desde horario base");
  };

  const save = async () => {
    if (!peritoId || !draft) return;
    setSaving(true);
    try {
      // Sólo enviamos los días marcados como sobreescritos. Si un día no
      // está en el body, el backend conserva el del horario base.
      const body = {
        iso_year: draft.iso_year,
        iso_week: draft.iso_week,
        label: draft.label,
      };
      DAYS.forEach(({ key }) => {
        const v = draft.days[key];
        if (v && v.enabled !== undefined) {
          body[key] = {
            enabled: !!v.enabled,
            start: v.start || "09:00",
            end: v.end || "18:00",
            lunch_start: v.lunch_start || null,
            lunch_end: v.lunch_end || null,
          };
        }
      });
      await api.put(`/calendar/schedule/${peritoId}/week-overrides`, body);
      toast.success("Excepción semanal guardada");
      setEditorOpen(false);
      setDraft(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la excepción");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ov) => {
    if (!window.confirm(`¿Eliminar la excepción de la semana ${ov.iso_week}/${ov.iso_year}?`)) return;
    try {
      await api.delete(`/calendar/schedule/${peritoId}/week-overrides/${ov.iso_year}/${ov.iso_week}`);
      toast("Excepción eliminada");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <section
      className="bg-white border border-border rounded-lg overflow-hidden"
      data-testid="week-overrides-card"
    >
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-zinc-50">
        <span className="icon-chip chip-icon-amber flex-shrink-0">
          <Calendar className="w-4 h-4" weight="regular" />
        </span>
        <div className="flex-1">
          <h3 className="section-title">Excepciones por semana</h3>
          <p className="help-text">
            Define horarios distintos al base por semanas concretas (ej.
            horario de verano). El base sigue aplicando fuera de esas semanas.
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="week-override-new-btn"
          className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-brand text-white hover:bg-brand/90 inline-flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" weight="bold" /> Nueva
        </button>
      </header>

      <div className="px-4 py-3">
        {overrides.length === 0 && (
          <div className="text-[12px] text-muted italic text-center py-3">
            Sin excepciones definidas. Tu horario base se aplica a todas las semanas.
          </div>
        )}
        {overrides.length > 0 && (
          <ul className="divide-y divide-border" data-testid="week-overrides-list">
            {overrides.map((ov) => {
              const days = DAYS.filter(({ key }) => ov[key]).map((d) => d.label).join(", ");
              return (
                <li
                  key={`${ov.iso_year}-${ov.iso_week}`}
                  data-testid={`week-override-${ov.iso_year}-${ov.iso_week}`}
                  className="py-2.5 flex items-center gap-3"
                >
                  <div className="w-14 h-14 rounded-md bg-amber-50 border border-amber-200 flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700">SEM</span>
                    <span className="text-[15px] font-bold text-amber-900 leading-none">{ov.iso_week}</span>
                    <span className="text-[9px] font-semibold text-amber-700 mt-px">{ov.iso_year}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-foreground">
                      {ov.label || <em className="text-muted">Sin nombre</em>}
                    </div>
                    <div className="text-[10.5px] text-muted capitalize">
                      Empieza el {fmtMonday(ov.iso_year, ov.iso_week)}
                    </div>
                    <div className="text-[10.5px] text-muted truncate">
                      Días redefinidos: {days || "ninguno"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => startEdit(ov)}
                      title="Editar"
                      data-testid={`week-override-edit-${ov.iso_year}-${ov.iso_week}`}
                      className="w-8 h-8 inline-flex items-center justify-center rounded border border-border text-muted hover:text-foreground hover:bg-zinc-50"
                    >
                      <PencilSimple className="w-3.5 h-3.5" weight="regular" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(ov)}
                      title="Eliminar"
                      data-testid={`week-override-delete-${ov.iso_year}-${ov.iso_week}`}
                      className="w-8 h-8 inline-flex items-center justify-center rounded border border-border text-muted hover:text-rose-700 hover:bg-rose-50"
                    >
                      <Trash className="w-3.5 h-3.5" weight="regular" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {editorOpen && draft && (
        <Editor
          draft={draft}
          setDraft={setDraft}
          onClose={() => { setEditorOpen(false); setDraft(null); }}
          onCopyBase={copyFromBase}
          onSave={save}
          saving={saving}
          existingKeys={new Set(overrides.map((o) => `${o.iso_year}-${o.iso_week}`))}
        />
      )}
    </section>
  );
}

function Editor({ draft, setDraft, onClose, onCopyBase, onSave, saving, existingKeys }) {
  const weeksInYear = useMemo(() => weeksInISOYear(draft.iso_year), [draft.iso_year]);
  const mondayLabel = useMemo(
    () => fmtMonday(draft.iso_year, draft.iso_week), [draft.iso_year, draft.iso_week],
  );
  const key = `${draft.iso_year}-${draft.iso_week}`;
  const isEditing = existingKeys.has(key);
  const monthName = useMemo(() => {
    try {
      const m = isoWeekToMonday(draft.iso_year, draft.iso_week);
      return m.toLocaleDateString("es-ES", { month: "long" });
    } catch { return ""; }
  }, [draft.iso_year, draft.iso_week]);

  const updDay = (key, patch) => {
    setDraft({ ...draft, days: { ...draft.days, [key]: { ...draft.days[key], ...patch } } });
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="week-override-editor"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="font-display font-bold">
            {isEditing ? "Editar excepción" : "Nueva excepción semanal"}
          </h2>
          <button onClick={onClose} className="text-muted hover:text-foreground" data-testid="week-override-editor-close">
            <X className="w-4 h-4" weight="bold" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          {/* Selectores */}
          <div className="grid grid-cols-1 sm:grid-cols-[110px_110px_110px_1fr] gap-2">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1">Año</span>
              <select
                value={draft.iso_year}
                onChange={(e) => setDraft({ ...draft, iso_year: parseInt(e.target.value, 10) })}
                data-testid="week-override-year"
                className="w-full text-sm border border-border rounded-md h-9 px-2 bg-white"
              >
                {[draft.iso_year - 1, draft.iso_year, draft.iso_year + 1, draft.iso_year + 2].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1">Mes (info)</span>
              <input
                type="text"
                value={monthName}
                readOnly
                tabIndex={-1}
                className="w-full text-sm border border-border rounded-md h-9 px-2 bg-zinc-50 capitalize"
              />
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1">Semana ISO</span>
              <select
                value={draft.iso_week}
                onChange={(e) => setDraft({ ...draft, iso_week: parseInt(e.target.value, 10) })}
                data-testid="week-override-week"
                className="w-full text-sm border border-border rounded-md h-9 px-2 bg-white"
              >
                {Array.from({ length: weeksInYear }, (_, i) => i + 1).map((w) => (
                  <option key={w} value={w}>Sem {w}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1">
                Empieza el lunes <span className="text-amber-700">{mondayLabel}</span>
              </span>
              <input
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="Etiqueta (opcional): ej. 'Verano · jornada intensiva'"
                data-testid="week-override-label"
                className="w-full text-sm border border-border rounded-md h-9 px-2"
              />
            </label>
          </div>

          {/* Botón copiar desde base */}
          <div className="flex items-center justify-between text-[11.5px]">
            <span className="text-muted">
              Marca el check de cada día que quieras sobrescribir. Los días no marcados usarán el horario base.
            </span>
            <button
              type="button"
              onClick={onCopyBase}
              data-testid="week-override-copy-base"
              className="text-brand font-semibold hover:underline inline-flex items-center gap-1"
            >
              <Copy className="w-3.5 h-3.5" weight="bold" /> Cargar desde horario base
            </button>
          </div>

          {/* Tabla días */}
          <div className="overflow-x-auto border border-border rounded-md">
            <table className="w-full text-[12.5px]">
              <thead className="bg-zinc-50 text-[10.5px]">
                <tr>
                  <th className="text-left py-2 px-2 font-semibold text-muted">Día</th>
                  <th className="text-center py-2 px-2 font-semibold text-muted">Trabaja</th>
                  <th className="text-center py-2 px-2 font-semibold text-muted">Inicio</th>
                  <th className="text-center py-2 px-2 font-semibold text-muted">Fin</th>
                  <th className="text-center py-2 px-2 font-semibold text-muted">Pausa inicio</th>
                  <th className="text-center py-2 px-2 font-semibold text-muted">Pausa fin</th>
                </tr>
              </thead>
              <tbody>
                {DAYS.map(({ key: dkey, label }) => {
                  const d = draft.days[dkey] || emptyDay();
                  return (
                    <tr key={dkey} className="border-t border-border">
                      <td className="py-1.5 px-2 font-medium">{label}</td>
                      <td className="py-1.5 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!d.enabled}
                          onChange={(e) => updDay(dkey, { enabled: e.target.checked })}
                          data-testid={`week-override-${dkey}-enabled`}
                        />
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <input type="time" value={d.start || ""} onChange={(e) => updDay(dkey, { start: e.target.value })}
                               disabled={!d.enabled}
                               className="!h-8 !text-[12px] !px-1.5 disabled:opacity-30 border border-border rounded" />
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <input type="time" value={d.end || ""} onChange={(e) => updDay(dkey, { end: e.target.value })}
                               disabled={!d.enabled}
                               className="!h-8 !text-[12px] !px-1.5 disabled:opacity-30 border border-border rounded" />
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <input type="time" value={d.lunch_start || ""} onChange={(e) => updDay(dkey, { lunch_start: e.target.value })}
                               disabled={!d.enabled}
                               className="!h-8 !text-[12px] !px-1.5 disabled:opacity-30 border border-border rounded" />
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <input type="time" value={d.lunch_end || ""} onChange={(e) => updDay(dkey, { lunch_end: e.target.value })}
                               disabled={!d.enabled}
                               className="!h-8 !text-[12px] !px-1.5 disabled:opacity-30 border border-border rounded" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-zinc-50 sticky bottom-0">
          <button
            onClick={onClose}
            className="text-[12px] font-semibold px-3 py-2 rounded-md text-muted hover:bg-zinc-100"
          >
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="week-override-save"
            className="text-[12.5px] font-semibold px-4 py-2 rounded-md bg-brand text-white hover:bg-brand/90 disabled:opacity-60"
          >
            {saving ? "Guardando…" : (isEditing ? "Guardar cambios" : "Crear excepción")}
          </button>
        </footer>
      </div>
    </div>
  );
}
